import React, {useState, useEffect} from 'react';
import View from './view';
import {EsiaRegCardForm, FormProps, KidSubform} from './model';
import {destroy, getFormValues, initialize, InjectedFormProps, reduxForm} from 'redux-form';
import {Forms} from '../../../../../shared/models/forms';
import {useSelector} from 'react-redux';
import {useAppDispatch, useAppSelector} from '../../../../../redux';
import {esiaRegistrationInfoSelector} from '../../../../../redux/selectors/login';
import {getApiRoute, validationPipe} from '../../../../../shared/pipes';
import {passportFormFieldNames, validationItems} from './constants';
import {validationItems as kidValidationItems} from './kidSubform/constants';
import {RegisterClientCabinetRequestPayload} from '../../../../../shared/models/api/registerClientCabinetRequestPayload';
import {loginSlice} from '../../../../../redux/reducers/login';
import {mapEsiaRegInfoToFormValues, mapFormValuesToEsiaRegData, mapFormValuesToEsiaRegInfo} from '../formMapper';
import {useRegCardState} from '../../esiaRegCardState/useRegCardState';
import {store} from '../../../../../redux';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {hasOnlyUniqueVals} from '../../../../../shared/utils/hasOnlyUniqueVals';
import {httpService, HttpWrapperOptions} from '../../../../../shared/httpWrapper';
import {apiUrls} from '../../../../../shared/constants/urls';
import {holderProfileSelector} from '../../../../../redux/selectors/profile';
import {getHttpErrorMessage} from '../../../../../shared/utils/getHttpErrorMessage';

const excludeHolderOwnContacts = (
  response: {duplicateEmails?: string[]; duplicateMobilePhones?: string[]},
  holder: {email?: string | null; mobilePhone?: string | null} | null | undefined,
) => {
  const normalizeEmail = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  const normalizePhone = (v: string | null | undefined) => (v ?? '').replace(/\D/g, '').replace(/^8/, '7');
  const ownEmails = new Set([normalizeEmail(holder?.email)].filter(Boolean));
  const ownPhones = new Set([normalizePhone(holder?.mobilePhone)].filter(Boolean));

  return {
    duplicateEmails: (response.duplicateEmails ?? []).filter((e) => !ownEmails.has(normalizeEmail(e))),
    duplicateMobilePhones: (response.duplicateMobilePhones ?? []).filter((p) => !ownPhones.has(normalizePhone(p))),
  };
};

const getFormErrors = (
  formData: Omit<
    RegisterClientCabinetRequestPayload,
    'accessToken' | 'counterpartyDepartmentId' | 'desiredAddress' | 'deliveryTypeId'
  >,
  skipPassportFields = false,
) => {
  const items = skipPassportFields
    ? validationItems.filter((item) => !passportFormFieldNames.includes(item.names as keyof EsiaRegCardForm))
    : validationItems;

  return validationPipe(formData, items);
};

const getKidFormErrors = (
  formData: Omit<
    RegisterClientCabinetRequestPayload,
    'accessToken' | 'counterpartyDepartmentId' | 'desiredAddress' | 'deliveryTypeId'
  >,
) => {
  return validationPipe(formData, kidValidationItems);
};

const _Form = ({handleSubmit, onEsiaSubmit, readOnly}: FormProps & InjectedFormProps<EsiaRegCardForm, FormProps>) => {
  const dispatch = useAppDispatch();

  const [submitting, _setSubmitting] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState(null);
  const onAcceptedToggle = () => setIsAccepted((prev) => !prev);

  const {parent, setConfirmedKids, checkedKids} = useRegCardState();
  const isReadOnly = Boolean(readOnly);

  const regInfo = useSelector(esiaRegistrationInfoSelector);

  //@ts-ignore
  const appReduxForms = useAppSelector((state) => state.form);
  useEffect(() => {
    return Object.keys(appReduxForms).forEach((form) => dispatch(destroy(form)));
  }, []);

  const formValues = useAppSelector((state) => {
    return getFormValues(Forms.esiaRegCardForm)(state) as EsiaRegCardForm;
  });

  const formsMobileNumbers: string[] = [];
  const formsEmails: string[] = [];
  const getKidsFormsErrors = () => {
    return checkedKids
      .map((kid, index, _arr) => {
        const kidFormValues = getFormValues(`${Forms.kidSubform}-${index}`)(store.getState()) as KidSubform;
        const submissionData = mapFormValuesToEsiaRegData(kidFormValues);
        if (submissionData.mobilePhone) formsMobileNumbers.push(submissionData.mobilePhone.slice(1));
        if (submissionData.email) formsEmails.push(submissionData.email);
        return getKidFormErrors(submissionData);
      })
      .filter((v) => v != null);
  };

  useEffect(() => {
    if (parent) {
      dispatch(initialize(Forms.esiaRegCardForm, mapEsiaRegInfoToFormValues(parent)));
    }
  }, [parent]);

  const onLocalSubmit = (e?: any) => {
    e?.preventDefault();
    handleSubmit(async () => {
      try {
        if (!regInfo) return;

        const submissionData = mapFormValuesToEsiaRegData(formValues, regInfo.token);

        if (submissionData.mobilePhone && parent?.checked) formsMobileNumbers.push(submissionData.mobilePhone.slice(1));
        if (submissionData.email && parent?.checked) formsEmails.push(submissionData.email);

        const errors = getFormErrors(submissionData, isReadOnly);

        if (errors) {
          console.error('errors', errors);
          setSubmissionErrors(errors);
          if (errors) {
            dispatch(
              addNotification({
                duration: 6000,
                message: Object.values(errors)
                  .map((v: any) => v.error)
                  .join(', '),
                type: SnackbarNotificationType.Error,
              }),
            );
          }
          return;
        }

        const kidsErrors = getKidsFormsErrors();
        if (kidsErrors?.length) {
          console.error('kidsErrors', kidsErrors);
          if (kidsErrors?.length) {
            dispatch(
              addNotification({
                duration: 6000,
                message: Object.values(kidsErrors[0])
                  .map((v: any) => v.error)
                  .join(', '),
                type: SnackbarNotificationType.Error,
              }),
            );
          }
          return;
        }

        const isEmailsUnique = hasOnlyUniqueVals(formsEmails);
        if (!isEmailsUnique) {
          dispatch(
            addNotification({
              duration: 6000,
              message: 'Эл. почта должна быть уникальныма',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }

        const isMobileNumbersUnique = hasOnlyUniqueVals(formsMobileNumbers);
        if (!isMobileNumbersUnique) {
          dispatch(
            addNotification({
              duration: 6000,
              message: 'Номера мобильных телефонов должны быть уникальными',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }

        const response = await httpService.post<{duplicateEmails: string[]; duplicateMobilePhones: string[]}>(
          getApiRoute(apiUrls.checkContactsUniqueness),
          {
            emails: formsEmails,
            mobilePhones: formsMobileNumbers,
          },
          new HttpWrapperOptions().Errors(true).ThrowException(true).Wait(true),
        );

        const holder = holderProfileSelector()(store.getState());
        const {duplicateEmails, duplicateMobilePhones} = excludeHolderOwnContacts(response, holder);

        if (duplicateEmails.length) {
          dispatch(
            addNotification({
              duration: 6000,
              message:
                'Данные email уже используются: ' +
                duplicateEmails.join(', ') +
                '. Измените email или обратитесь в тех. поддержку',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }
        if (duplicateMobilePhones.length) {
          dispatch(
            addNotification({
              duration: 6000,
              message:
                'Данные телефоны уже используются: ' +
                duplicateMobilePhones.join(', ') +
                '. Измените или обратитесь в тех. поддержку',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }

        const updatedRegInfo = mapFormValuesToEsiaRegInfo(formValues, regInfo.token, {
          cardStatusId: regInfo.cardStatusId ?? parent?.cardStatusId,
          cardApplicationStatusId: regInfo.cardApplicationStatusId ?? parent?.cardApplicationStatusId,
          cardDeliveryInfo: regInfo.cardDeliveryInfo,
        });
        const formDataKids = checkedKids.map((kid, kidIndex) => {
          const formData = getFormValues(`${Forms.kidSubform}-${kidIndex}`)(store.getState()) as KidSubform;
          return formData;
        });
        setConfirmedKids(formDataKids);
        dispatch(loginSlice.actions.setEsiaRegistrationInfo(updatedRegInfo));
        onEsiaSubmit?.();
      } catch (ex) {
        const errorText = getHttpErrorMessage(ex, 'Ошибка. Обратитесь в тех поддержку');
        dispatch(
          addNotification({
            duration: 6000,
            message: errorText,
            type: SnackbarNotificationType.Error,
          }),
        );
      }
    })();
  };

  return (
    <View
      formValues={formValues}
      isAccepted={isAccepted}
      onAcceptedToggle={onAcceptedToggle}
      loading={false}
      submissionErrors={submissionErrors}
      setSubmissionErrors={setSubmissionErrors}
      onSubmit={onLocalSubmit}
      submitting={submitting}
      readOnly={isReadOnly}
      // regInfo={regInfo}
    />
  );
};

export const EsiaRegCardFormScreen = reduxForm<EsiaRegCardForm, FormProps>({
  form: Forms.esiaRegCardForm,
  destroyOnUnmount: false,
  keepValues: true,
})(_Form);
